import { getSerialized } from '@extension/wa-communication-layer/core/utils/wa-key-serialization';
import { fetchFreeLimit } from "@common-reducers/free-limit-slice";
import { clientClockFields } from "@common/services/client-clock-fields";
import { replyForRecipient } from './reply-for-recipient';
import { backendApi } from "@common-reducers/backend-api/backend-api";
import { derivePerRecipientIdempotencyKey } from "./per-recipient-idempotency-key";
import { planEditFanOut, editedDocLeftBehind, type EditFanOutMessage } from "./plan-edit-fan-out";
import { fanOutScheduleRecipients } from "./fan-out-schedule-recipients";
import { toastDBCreateError } from "@common/utils/db-create-error-toast";
import i18n from "i18next";
import { namespace } from "@common-config/constants";
import { applySeedToDraft, ComposerSeed } from "./apply-seed-to-draft";
import { normalizeScheduledDueDate } from "@common/utils/scheduled-due-date";
import { UserMessage } from '@bt-common/schemas/bt/user-message';
import { ExtendedUserMessage } from '@common/models/extend-user-message';
import type { MongoUpdate } from '@common/types/mongo-update';

import { DBBoardsThunks, DBUserMessageThunks, userMessagesSelectByQuery, boardsSelectByQuery, userMessageReducerActions, isTempId } from "@common-reducers/DBServiceThunks";
import { getWaChatContactDisplayName } from "@/common/utils/display-name-utils";
import { setAddMsgInCalendarContact, setHighlightedContactId, setIsReply, setMentions } from "@common-reducers/WhatsAppReducer";
import { ProductEvent, trackProductEvent } from "@common-services/product-analytics";
import { getMessageFromInputField } from "@common-services/utils";
import { RRuleRecurrenceHandler } from "@common-services/utils/rrule-recurrence-handler";
import { applyRecurringEditPlan } from "./recurring-edit-plan";
import { storeRecurringReplacement } from "./recurring-replacement";
import { buildRecurringEditPatchFields } from "./recurring-edit-patch-document";
import { getRandomMessageColor } from "@common-views/calendar/message-color";
import { appConfig } from "@common/config/app.config";
import { accessFormPopupActions } from "@common/reducers/AccessFormPopupReducer";
import { addScheduledMessageButtonActions, selectScheduleRecipients } from "@common/reducers/AddScheduledMessageButtonReducer";
import { getCampaignStuckMessages } from "@common/reducers/CampaignSelectors";
import { mainSliceActions } from "@common/reducers/MainReducer";
import { taskActions } from "@common/reducers/TasksReducer";
import { btLayoutSidebarActions } from "@common-reducers/BtLayoutSidebarReducer";
import { userMessageActions } from "@common/reducers/UserMessageReducer";
import { setSelectedBoardId } from "@common-reducers/BoardsThunks";
import { scheduledMessageEditorActions } from "@common/reducers/ScheduledMessageEditorReducer";
import { selectUser, selectMessagesForCurrentChat, selectClearableMessagesForCurrentChat } from "@common/reducers/UserSelectors";
import { selectProfilePic } from "@common/reducers/WhatsAppSelectors";
import { selectMergedContactMap } from "@common/reducers/WhatsAppReducer";
import { resolveSenderJidEquivalents } from "@common/utils/jid-equivalents";
import { staleChatMessageIds } from "@common/services/chat-messages-refresh";
import { AnalyticsService } from "@common/services/analytics-service";
import { AppThunkDispatch, CommonRootState } from "@common/types/common-root-state-type";
import { MessageState } from "@common/types/interface";
import { AddScheduledMessageButtonState } from "@extension/context/content/components/add-scheduled-message-button/add-scheduled-message-button";
import { ContentMessageServiceActions } from "@extension/context/content/services/contentMessageServiceActions";
import { subscribeCurrentBoardToSocket } from "@extension/context/content/thunks/WhatsappThunks";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { PLANNER_LOAD_PLAN } from "@common/services/planner-filters";
import { loadQueue, startHorizonTick, type FindFn } from "./user-message-paging";
import { withFullThumbnailForCreate } from "@common/services/lazy-thumbnail";
import { fetchUserMessageById } from "@common/services/fetch-user-message-by-id";
import moment from "moment";
import randomstring from "randomstring";


const analyticsService = new AnalyticsService();
const handler = new RRuleRecurrenceHandler();

// Group related thunks together
// `_id` is optional at the thunk boundary: the thunk dispatches DBUserMessageThunks.create
// when `_id` is absent, .patch when present.
export type ScheduleMessageThunkArg = Omit<ExtendedUserMessage, '_id'> & {
    _id?: string;
    /**
     * FE#444 — set by "Schedule again" for its bulk copies. A copy is not composed in the composer,
     * so it must not borrow the composer's state: no quote from the open reply box, no click on the
     * footer's reply-close icon, no closing of the media-preview window. Never persisted.
     */
    skipComposerSideEffects?: boolean;
};

// Resolves with the created/patched scheduled message (or an array of them in the
// multi-recipient flow) so callers — the diagnostics runner, tools, tests — can
// verify what was stored (id, dueDate, status) instead of getting `undefined`.
export const scheduleMessageTool = createAsyncThunk<
    UserMessage | UserMessage[] | undefined,
    ScheduleMessageThunkArg,
    { state: CommonRootState; dispath: AppThunkDispatch }
>("scheduleMessage", async (_arg, thunkApi) => {
    // The flag steers THIS call only; it is taken off before the message is spread anywhere, so it
    // can never reach the stored document. The fan-out below passes it on explicitly.
    const { skipComposerSideEffects = false, ..._message } = _arg;
    console.log('[MULTIPLE RECIPIENTS] scheduleMessage thunk called with message:', _message);
    console.log('[MULTIPLE RECIPIENTS] _message.contactList:', _message.contactList);

    const state = thunkApi.getState()
    // The open reply box quotes a message of ONE chat: attach it only to a
    // message for that chat — a stale reply left open while scheduling to
    // another chat, or a fan-out, must not fail at send time.
    const replyData = skipComposerSideEffects
        ? null
        : await replyForRecipient(state.WhatsAppReducer.isReply, _message.contactList)
    const hasMediaAttachment = !skipComposerSideEffects && Boolean(_message?.imageId || _message?.asset?.url);

    const isEditing = !!_message._id;
    const isMultiRecipient = !!_message.contactList && _message.contactList.length > 1;

    // Editing: update the documents that already exist for these recipients instead of
    // re-creating them (re-creating duplicated the send). Planned for ANY recipient count: an
    // edit that moves a single-recipient message to a group which already has a copy must patch
    // that copy, not add a second one. See plan-edit-fan-out.ts.
    const editPlan = isEditing && _message.contactList && _message.contactList.length > 0
        ? planEditFanOut(
            _message.contactList,
            _message._id!,
            userMessagesSelectByQuery(state.DBUserMessageReducer, {}) as unknown as EditFanOutMessage[],
        )
        : null;
    // A lone recipient that just patches the edited document itself needs no fan-out: it falls
    // through to the single-contact flow below (this is also what stops the per-recipient
    // recursion from planning again).
    const isPlainSingleEdit = !isMultiRecipient && !!editPlan
        && editPlan.length === 1 && editPlan[0].action === 'patch'
        && (editPlan[0] as { _id: string })._id === _message._id;

    // If multiple contacts, create separate message for each contact
    if (isMultiRecipient || (editPlan && !isPlainSingleEdit)) {
        console.log(`[MULTIPLE RECIPIENTS] Fan-out for ${_message.contactList!.length} recipient(s), one message per recipient`);

        // FE#230: fan out through a resilient helper so a rejection on ONE recipient
        // can no longer abort the loop and silently drop every recipient after it
        // (the compose footer dispatches this fire-and-forget, so a mid-loop throw
        // gave the user no feedback either). Every recipient is attempted; the
        // helper rethrows only when EVERY recipient failed.
        const perContactResults: UserMessage[] = await fanOutScheduleRecipients<UserMessage>(
            // Defined here: both branch conditions require it (isMultiRecipient checks it, and
            // editPlan is only built for a non-empty contactList), but TS can't see through editPlan.
            _message.contactList!,
            // Explicit return type breaks the self-referential inference cycle (the
            // recursive dispatch of scheduleMessageTool would otherwise make this
            // payloadCreator's return type circular).
            async (contact, i): Promise<UserMessage | UserMessage[] | undefined> => {
                console.log(`[MULTIPLE RECIPIENTS] Processing message ${i + 1}/${_message.contactList!.length} for contact:`, contact);

                // Editing: PATCH the existing document for this recipient (the edited one, or
                // its sibling); only recipients with no document yet are CREATED.
                const patchId = editPlan?.[i]?.action === 'patch' ? (editPlan[i] as { _id: string })._id : undefined;
                const shouldPatchContact = !!patchId;

                // #270: the draft came from a lean row; the BE restores an omitted thumbnail on patch
                // but NOT on create, so recipients 2..N would be stored without the preview image.
                const sourceMessage = shouldPatchContact
                    ? _message
                    : await withFullThumbnailForCreate(_message, _message._id, fetchUserMessageById(thunkApi.dispatch as AppThunkDispatch));

                const messageForContact: ScheduleMessageThunkArg = {
                    ...sourceMessage,
                    contactList: [contact], // Only one contact per message
                    // FE#204: per-recipient idempotency key. Reusing the single batch key for every
                    // recipient collided on the unique clientIdempotencyKey index, so only the first
                    // group persisted; suffixing the recipient id lets all N persist (still idempotent
                    // per recipient on re-submit).
                    clientIdempotencyKey: derivePerRecipientIdempotencyKey(_message.clientIdempotencyKey, contact?.id, i),
                    _id: patchId,
                    skipComposerSideEffects,
                } as ExtendedUserMessage;

                if (shouldPatchContact) {
                    console.log(`[MULTIPLE RECIPIENTS] PATCH - Updating existing message ${patchId} with contact:`, contact);
                } else {
                    console.log(`[MULTIPLE RECIPIENTS] CREATE - Creating new message for contact:`, contact);
                }

                // Recursively call scheduleMessageTool for this single contact
                return thunkApi.dispatch(scheduleMessageTool(messageForContact)).unwrap();
            },
            // #1257: some recipients persisted, so the thunk resolves; tell the user how many did not.
            ({ failed, total, firstError }) => {
                toastDBCreateError(firstError, i18n.t('scheduleFanOut.recipientsNotSaved', { ns: namespace, failed, total, defaultValue: '{{failed}} of {{total}} recipients were not saved.' }));
            },
        );

        // The edited document's recipient was removed and nothing re-used it: delete it (same path
        // as the Delete button) so it does not still send the old text to the removed recipient.
        const leftBehindId = editPlan ? editedDocLeftBehind(editPlan, _message._id!) : undefined;
        if (leftBehindId) {
            console.log(`[MULTIPLE RECIPIENTS] DELETE - Edited message ${leftBehindId} lost its recipient`);
            // deleteUserMessage swallows a rejected delete, so dispatch the DB thunk directly and
            // check its outcome: a failed cleanup leaves a pending message that still sends.
            const deleteResult = await thunkApi.dispatch(DBUserMessageThunks.delete({ entity: { _id: leftBehindId } }) as any) as
                { meta?: { requestStatus?: string }; error?: { message?: string } } | undefined;
            if (deleteResult?.meta?.requestStatus === 'rejected' || deleteResult?.error) {
                console.error(`[MULTIPLE RECIPIENTS] Failed to delete edited message ${leftBehindId} after its recipient was removed; it is still pending and will send the old text`, deleteResult?.error);
            }
        }

        console.log('[MULTIPLE RECIPIENTS] Finished processing all contacts');
        // A one-recipient edit keeps the single-message result shape.
        return isMultiRecipient ? perContactResults : perContactResults[0]; // Exit early since we've handled all contacts
    }

    // Single contact flow (original logic)
    console.log('[MULTIPLE RECIPIENTS] Single contact flow');

    const fixedRruleset = _message.rruleset;

    let poll
    if (_message.isPoll) {
        poll = {
            ..._message.poll,
            options: (_message.poll?.options ?? [])
                .filter(o => o.text && o.text !== ''),
        }
    } else {
        poll = {
            question: '',
            options: [
                { id: Math.floor(Math.random() * 1000000000000000000000000), text: '', order: 0 },
                { id: Math.floor(Math.random() * 1000000000000000000000000), text: '', order: 1 },
            ],
            allowMultiple: false,
        }
    }

    // BE#657 — resolve the authoritative dueDate WITHOUT fabricating "now". `moment(undefined)`
    // is `now`, so the old `moment(_message.dueDate)` silently armed an immediate send for a
    // date-less scheduled draft (which then fired before the intended future date was applied).
    // A scheduled message with no valid dueDate is invalid — refuse it rather than send it now.
    const normalizedDue = normalizeScheduledDueDate(_message.dueDate);
    if (!normalizedDue) {
        throw new Error('[scheduleMessage] refusing to schedule a message without a valid dueDate (BE#657) — an unset/invalid dueDate would fire immediately');
    }

    const message: ScheduleMessageThunkArg = {
        ..._message,
        id: randomstring.generate({ length: 8, charset: "alphanumeric" }),
        dueDate: moment(normalizedDue).startOf("minute").toDate(),
        status: 'pending',
        state: MessageState.pending,
        replyData,
        poll,
        messageColor: _message.messageColor ? _message.messageColor : getRandomMessageColor(),
        rruleset: fixedRruleset, // Apply the fixed rruleset
    };


    if (message?.replyData) {
        setTimeout(() => {
            const iconElement = document.querySelectorAll("footer [data-icon='x']")[1];

            if (iconElement instanceof HTMLElement) {
                iconElement.click();
            }
        }, 0); // A delay of 0ms to defer the action until the next tick
    }

    const firstContact = message.contactList?.[0];
    const firstContactId = firstContact?.id ?? '';
    const profilePic = await selectProfilePic(state, firstContactId);
    // Invariant: firstContact must exist for a scheduled message; the dispatcher always supplies one.
    const updatedContact = { ...firstContact!, profilePicUrl: profilePic };
    let storedMessage: UserMessage | undefined;
    if (message._id) {
        // BE#657 m3 — strip the UI-only flag from the patch payload too.
        const { dueDateSetByUser: _omitFlagP, ...messageForPatch } = message as typeof message & { dueDateSetByUser?: boolean };
        storedMessage = await thunkApi.dispatch<any>(DBUserMessageThunks.patch({
            entity: {
                ...messageForPatch,
                contactList: [updatedContact],
                // FE#447: this machine's clock, so the server can record the creator's skew next to the
                // due time just set (request-only; never stored).
                ...clientClockFields(),
            },
            // #1257: the patch now rejects when nothing was saved, and the caller says so. Put the
            // stored copy back too, or the list keeps showing the edit the toast says was not saved.
            options: { inCaseOfErrorRollbackOptimisticUpdateDisabled: true },
        })).unwrap();
        trackProductEvent(ProductEvent.UpdateScheduledMessage, {
            category: "Schedule",
            action: "update scheduled message",
        });
    } else {
        message.disableOptimisticUpdates = true;
        // _id is omitted intentionally on create — backend assigns it. Strip it
        // explicitly: a falsy-but-present _id (e.g. "") survives JSON serialization
        // and makes the backend fail ObjectId casting. The DBThunk's generic
        // UserMessage requires _id, so widen here at the boundary.
        // BE#657 m3 — also drop the UI-only `dueDateSetByUser` marker (mongoose strips
        // it today, but don't rely on that; keep it client-side like `_id`).
        const { _id: _omittedId, dueDateSetByUser: _omitFlag, ...messageWithoutId } = message as typeof message & { dueDateSetByUser?: boolean };
        storedMessage = await thunkApi.dispatch<any>(DBUserMessageThunks.create({
            ...messageWithoutId,
            contactList: [updatedContact],
            // FE#447: this machine's clock — see the patch above.
            ...clientClockFields(),
        } as UserMessage)).unwrap();
        trackProductEvent(ProductEvent.ScheduleMessage, {
            category: "Schedule",
            action: "schedule message",
            message_type: message.isRecurring ? 'recurring' : 'scheduled',
            is_recurring: !!message.isRecurring,
        });

        // #131 T2 — the user just consumed a monthly-quota slot, so re-read the policy.
        // WITHOUT THIS the ten_per_month arm is effectively an unlimited free plan: that arm
        // has NO concurrent cap, so `monthlyUsed` is its ONLY cap, and a policy fetched once
        // and never refreshed makes the quota "10 per page reload", not 10 per month.
        // Fire-and-forget: a failed refresh must never break a successful schedule.
        try {
            thunkApi.dispatch(fetchFreeLimit());
        } catch {
            /* never break the send path over a policy refresh */
        }

        // #113 FE-1 — a completed schedule may satisfy a first-week-checklist step
        // (self_test / workflow); refetch so the card advances without a manual
        // reload. Fire-and-forget, same rationale as the free-limit refresh above.
        try {
            thunkApi.dispatch(backendApi.endpoints.getFirstWeekChecklist.initiate(undefined, { forceRefetch: true }));
        } catch {
            /* never break the send path over a checklist refresh */
        }
    }

    if (hasMediaAttachment) {
        try {
            await ContentMessageServiceActions.closeMediaPreview();
        } catch (error) {
            console.warn('Failed to close media preview window:', error);
        }
    }

    return storedMessage;
});

// Backward compatibility for existing imports/usages.
export const scheduleMessage = scheduleMessageTool;

// Recurring message handling
export const handleRecurringMessageEditThunk = createAsyncThunk<
    void,
    {
        scope: 'all' | 'this' | 'thisAndFollowing'
    },
    { state: CommonRootState }
>("userMessages/handleRecurringMessageEdit", async ({ scope }, { dispatch, getState }) => {

    const state = getState() as CommonRootState;
    const message = state.AddScheduledMessageButtonReducer.messageDraft;
    const messageList = userMessagesSelectByQuery(state.DBUserMessageReducer, {
        $or: [{ deleted: { $exists: false } }, { deleted: false }],
    }, undefined, undefined)

    // FE#230: read the recipient list from the single source of truth
    // (selectScheduleRecipients), NOT from WhatsAppReducer.addMsgInCalendarContact.
    // editCalendarMessage seeds `recipients` from the edited message but never
    // sets addMsgInCalendarContact, so the old single-contact path resolved to an
    // empty [{id:''}] on a calendar recurring-edit + Schedule Send when the picker
    // was not re-touched — silently dropping the recipient on the replacement.
    let currentMessage = {
        ...message,
        contactList: selectScheduleRecipients(state),
    };
    const newDate = new Date(currentMessage.dueDate ?? Date.now())

    const originalMessage = messageList.find((m) => m._id === currentMessage._id);
    if (!originalMessage?.rruleset) {
        return;
    }

    try {
        const state = getState() as CommonRootState;

        if (!state.CalendarReducer.selectedEventDate) {
            return;
        }
        // CRITICAL FIX: Use the clicked instance date stored by editCalendarEventThunk
        const clickedDate = new Date(state.CalendarReducer.selectedEventDate);

        // CRITICAL FIX: Generate proper newRuleStr from currentMessage
        const newRuleStr = currentMessage.isRecurring ? (currentMessage.rruleset || '') : '';

        const result = handler.handleRecurringEdit(
            originalMessage.rruleset,  // Original recurrence rule
            clickedDate,               // The specific instance that was clicked
            newDate,                   // The new date/time for that instance
            newRuleStr,                // The new recurrence rule (if any)
            scope,                     // The scope of the edit
            new Date(originalMessage.dueDate ?? Date.now()) // CRITICAL FIX: Pass originalMessage.dueDate for correct originalDueDate calculation
        );

        // FE#151: create and confirm the replacement BEFORE removing or rewriting the original.
        // These were previously two independent, unawaited dispatches, so a create that failed
        // left the customer with neither message — and because nothing awaited it, the rejection
        // surfaced only as an unhandled promise rejection, never as an error they could see.
        await applyRecurringEditPlan(result, {
            createReplacement: async () => {
                // #270: the draft's preview came from a lean row; restore its stored thumbnail (BE does not on create).
                const draft = await withFullThumbnailForCreate(currentMessage, originalMessage._id, fetchUserMessageById(dispatch));
                const newMessage: ScheduleMessageThunkArg = {
                    ...draft,
                    _id: undefined,
                    // NOTE — deliberately NOT re-derived from the original's key here, unlike
                    // the DRAG path. `currentMessage` is the compose DRAFT, and the draft's
                    // `clientIdempotencyKey` is minted fresh at dialog-open (BE#372/#231,
                    // AddScheduledMessageButtonReducer) precisely so an edit never inherits the
                    // stored key. It is already a correct new-document token, and it is the one
                    // that makes a double-clicked Save dedupe. Deriving from the original would
                    // be strictly worse: the backend strips `clientIdempotencyKey` from every
                    // recurring successor (`stripInheritedIdentity`, next-occurrence.ts), so for
                    // any series that has already fired the original has no key, the derivation
                    // yields undefined, and the fan-out loses its dedup protection entirely.
                    dueDate: result.newDueDate ? new Date(result.newDueDate) : undefined,
                    rruleset: result.newRRule,
                    isRecurring: !!result.newRRule,
                    skipDayMappingFix: true,
                } as ScheduleMessageThunkArg;
                // #1133: drops the series' parentMessageId and resolves only for a STORED doc.
                return storeRecurringReplacement(newMessage, doc => dispatch(scheduleMessage(doc)).unwrap());
            },
            deleteOriginal: () => dispatch(deleteUserMessage(originalMessage._id)).unwrap(),
            patchOriginal: () => {
                // A patch that comes WITHOUT a replacement is the edit itself — "all",
                // and first-occurrence "this and following" now that it re-anchors the
                // series in place rather than destroying it. Patching from the stored
                // document there would move the schedule and silently discard what the
                // customer typed. A patch that comes WITH a replacement is the untouched
                // remainder of the series and must keep its old content.
                // See recurring-edit-patch-document.ts.
                const patchedMessage = buildRecurringEditPatchFields(
                    result,
                    originalMessage as any,
                    currentMessage as any,
                ) as ScheduleMessageThunkArg;
                return dispatch(scheduleMessage(patchedMessage)).unwrap();
            },
        });

    } catch (error) {
        throw error;
    }
});

type HandleClickScheduleMessageThunkParams = {
    isSchedulePollButton?: boolean;
    // #113 FE-1 — optional composer seed (first-week-value card / deep link):
    // pre-fills the recipient/dueDate/guided flag on the draft. See
    // applySeedToDraft for the pure assignment logic.
    seed?: ComposerSeed;
}

export const handleClickScheduleMessageThunk = createAsyncThunk<
    void,
    HandleClickScheduleMessageThunkParams,
    { state: CommonRootState }
>("scheduleMessage", async (HandleClickScheduleMessageThunkParams: HandleClickScheduleMessageThunkParams, thunkApi) => {
    const thunkStartTime = performance.now();
    console.log(`[Performance] handleClickScheduleMessageThunk started at ${thunkStartTime.toFixed(2)}ms`);

    if (typeof performance !== 'undefined' && performance.mark) {
        performance.mark('schedule-message-thunk-start');
    }

    const state: CommonRootState = thunkApi.getState();
    const dispatch = thunkApi.dispatch;
    const { isSchedulePollButton, seed } = HandleClickScheduleMessageThunkParams
    const user = selectUser(state)
    const _currentChat = state.WhatsAppReducer.currentChat;
    const currentChat = {
        ..._currentChat,
        participants: undefined,
    }

    // _id is omitted from the seed message — the editor will create one on schedule.
    const resetState: AddScheduledMessageButtonState = {
        showErrorDialog: false,
        open: false,
        message: {
            dueDate: undefined,
            message: "",
            isRecurring: false,
            state: MessageState.pending,
            cancelIfReceived: false,
        } as UserMessage,
        isEmojiPicker: false,
    };

    if (!user) {

        analyticsService.event("login_before_schedule", {
            category: "Schedule",
            action: "login before schedule",
        });

        dispatch(accessFormPopupActions.setStatus('Login'));
        return;
    }

    let message: ScheduleMessageThunkArg = Object.assign({}, resetState.message) as ScheduleMessageThunkArg;
    // #113 FE-1 — applySeedToDraft is behavior-preserving when `seed` is undefined:
    // same current-chat contact resolution + dueDate≈now this thunk always did.
    message = applySeedToDraft(message, seed, currentChat);
    if (isSchedulePollButton) {
        ContentMessageServiceActions.getPollData()
    } else {
        message.message = getMessageFromInputField();
    }

    message.isTimeValid = true;

    const analyticsStartTime = performance.now();
    analyticsService.event("open_schedule_dialog", {
        category: "Schedule",
        action: "open dialog",
    });
    const analyticsTime = performance.now() - analyticsStartTime;
    console.log(`[Performance] Analytics event sent in ${analyticsTime.toFixed(2)}ms`);

    const dispatchStartTime = performance.now();
    // `message` carries no _id (new draft); the editor reducer accepts the
    // wider ExtendedUserMessage but tolerates absent _id at runtime.
    dispatch(scheduledMessageEditorActions.loadMessageData(message as ExtendedUserMessage));
    dispatch(addScheduledMessageButtonActions.editMessage(message as ExtendedUserMessage));
    const dispatchTime = performance.now() - dispatchStartTime;
    console.log(`[Performance] editMessage action dispatched in ${dispatchTime.toFixed(2)}ms`);

    const totalThunkTime = performance.now() - thunkStartTime;
    console.log(`[Performance] handleClickScheduleMessageThunk completed in ${totalThunkTime.toFixed(2)}ms`);

    if (typeof performance !== 'undefined' && performance.measure) {
        performance.measure('schedule-message-thunk-total', 'schedule-message-thunk-start');
    }

    // dispatch(clearChatInputThunk());
});



export const toggleTasksThunk = createAsyncThunk<
    void,
    void, // No args needed really, or keep UserMessage if used (it wasn't used in body except type)
    { state: CommonRootState }
>("scheduleMessage/toggleTasks", async (_, thunkApi) => {
    const state: CommonRootState = thunkApi.getState();
    const dispatch = thunkApi.dispatch;
    const user = selectUser(state)
    // const isTasksPanelOpen = state.TasksReducer.isTasksPanelOpen
    const { isOpen, componentToShow } = state.BtLayoutSidebarReducer;
    const isTasksSidebarOpen = isOpen && componentToShow === 'Tasks';

    const boardsLoaded = state.DBBoardsReducer.itemsLoaded

    if (!user) {
        dispatch(accessFormPopupActions.setStatus('Login'))
        return;
    }

    if (isTasksSidebarOpen) {
        dispatch(btLayoutSidebarActions.closeSidebar());
        return;
    }

    const boardList = boardsSelectByQuery(state.DBBoardsReducer, {}, undefined, undefined);

    // Check if we have a current chat and if a board already exists for it locally
    const currentChat = state.WhatsAppReducer.currentChat;
    const currentChatId = getSerialized(currentChat?.id);

    console.log("[board-id] toggleTasksThunk run. currentChatId:", currentChatId, "boardsLoaded:", boardsLoaded);

    const existingBoard = boardList.find(b =>
        !!currentChatId && b._IdByWaChatIds?.includes(currentChatId) && b.isWABoard
    );

    console.log("[board-id] LOGS START");
    console.log("[board-id] typeof currentChatId:", typeof currentChatId);
    console.log("[board-id] currentChatId value:", `"${currentChatId}"`);
    console.log("[board-id] boardList length:", boardList.length);
    console.log("[board-id] existingBoard found?:", !!existingBoard);
    console.log("[board-id] check: !existingBoard && currentChatId =", !existingBoard && currentChatId);
    console.log("ToggleTasksButton: run only once");
    // If no board found locally but we have a chat ID, try to fetch it
    if (!existingBoard && currentChatId) {
        console.log("[board-id] ENTERING FETCH BLOCK");
        console.log("No local board found for this chat, fetching from server...");
        dispatch(DBBoardsThunks.find({
            _IdByWaChatIds: currentChatId,
            $limit: 1
        })).then((res: any) => {
            console.log("[board-id] fetch result:", res);

            // Re-query state to find the board, as it should be in the store now
            const stateAfterFetch: CommonRootState = thunkApi.getState();
            const boardListAfterFetch = boardsSelectByQuery(stateAfterFetch.DBBoardsReducer, {}, undefined, undefined);
            const fetchedBoard = boardListAfterFetch.find(b =>
                !!currentChatId && b._IdByWaChatIds?.includes(currentChatId) && b.isWABoard
            );

            if (fetchedBoard?._id) {
                console.log("[board-id] found board via re-query:", fetchedBoard._id);
                dispatch(setSelectedBoardId({ boardId: fetchedBoard._id }));
            } else if (res?.payload?.data && res?.payload?.data?.length > 0) {
                const board = res.payload.data[0];
                console.log("[board-id] fetched board (payload):", board._id);
                dispatch(setSelectedBoardId({ boardId: board._id }));
            }

            dispatch(subscribeCurrentBoardToSocket())
            // dispatch(taskActions.setIsTasksPanelOpen(!isTasksPanelOpen));
            dispatch(btLayoutSidebarActions.openWithComponent('Tasks'));
        });
        return; // Return here, let the promise resolution open the panel
    }

    // Existing board found
    if (existingBoard?._id) {
        console.log("[board-id] found existing board:", existingBoard._id);
        dispatch(setSelectedBoardId({ boardId: existingBoard._id }));
    }

    // Original logic for when we believe we have everything or just generic load
    if (!boardsLoaded) {
        console.log("get boards");
        dispatch(DBBoardsThunks.find({})).then(() => {
            dispatch(subscribeCurrentBoardToSocket())

            // Re-check for board after fetch
            const stateAfterFetch: CommonRootState = thunkApi.getState();
            const boardListAfterFetch = boardsSelectByQuery(stateAfterFetch.DBBoardsReducer, {}, undefined, undefined);
            const existingBoardAfterFetch = boardListAfterFetch.find(b =>
                !!currentChatId && b._IdByWaChatIds?.includes(currentChatId) && b.isWABoard
            );

            if (existingBoardAfterFetch?._id) {
                console.log("[board-id] found existing board after fetch:", existingBoardAfterFetch._id);
                dispatch(setSelectedBoardId({ boardId: existingBoardAfterFetch._id }));
            }

            dispatch(btLayoutSidebarActions.openWithComponent('Tasks'));
        });
        return;
    }

    // dispatch(taskActions.setIsTasksPanelOpen(!isTasksPanelOpen));
    dispatch(btLayoutSidebarActions.openWithComponent('Tasks'));
});



// CREATE THUNK getUserMessagesAndCampaigns
// Boot loader for the scheduled-messages sidebar/calendar surface. Loads the
// unbounded pending/failed queue, AND (#166) the unconfirmed tail — messages
// handed to WhatsApp with no delivery confirmation yet (`sent_pending_ack`,
// plus legacy `sent`) — so the widened display selector has rows to select.
// Dispatched from three call sites (UserThunks.ts, calendar-view.tsx,
// WhatsappThunks.ts); wiring both fetches here keeps all three consistent.
export const getPendingUserMessages = createAsyncThunk(
    "getUserMessages",
    async (_, { dispatch }) => {
        console.log("getPendingUserMessages");
        // #270: paged lean load (Tier 1 = send-critical, then the rest of the queue) with a legacy
        // unbounded fallback; see user-message-paging.ts. The status list is still derived from
        // PLANNER_LOAD_PLAN there, so a status the planner can show is never one nothing fetches.
        const findUserMessages: FindFn = (query) => dispatch(DBUserMessageThunks.find(query)).unwrap();
        const pendingFailedPromise = loadQueue(findUserMessages).then((rows) => {
            startHorizonTick(findUserMessages);
            return rows;
        });

        // #166/#169 — the unconfirmed tail: handed to WhatsApp, no delivery
        // confirmation. #166 fetched it as a GLOBAL newest-500 ($limit:500), so a
        // heavy account (the heaviest holds 12,871 of these) only ever loaded its
        // newest 500 — every OLDER unconfirmed row was absent from the store and
        // therefore read as CONFIRMED in the badge/status logic (#169). Fix:
        // PAGINATE newest-first (fixed page size, increasing $skip) until a short
        // page, so all unconfirmed rows load regardless of count. Bounded by a
        // generous page cap that LOGS if reached — never a silent drop.
        // NOTE: `$and` is not in the endpoint's operator whitelist — it 400s;
        // deleted rows are excluded via `$or`, page bound via $skip/$limit.
        //
        // Kicked off here (its first page is dispatched BEFORE we await the
        // pending/failed load above) so the two loads are independent: a rejected
        // pending/failed load must not skip this tail, and vice versa. Awaited at
        // the end so a heavy account finishes loading before the thunk resolves.
        const UNCONFIRMED_TAIL_PAGE_SIZE = 500;
        const UNCONFIRMED_TAIL_MAX_PAGES = 60; // ~30k rows; logs (never silently drops) if reached
        const loadUnconfirmedTail = async () => {
            for (let page = 0; page < UNCONFIRMED_TAIL_MAX_PAGES; page++) {
                const batch = await dispatch(
                    DBUserMessageThunks.find({
                        $or: [
                            { deleted: { $ne: true } },
                        ],
                        status: {
                            $in: [...PLANNER_LOAD_PLAN.unconfirmedTail]
                        },
                        $sort: {
                            dueDate: -1,
                        },
                        $skip: page * UNCONFIRMED_TAIL_PAGE_SIZE,
                        $limit: UNCONFIRMED_TAIL_PAGE_SIZE,
                        "contactList.0._id": { $exists: true },
                    })
                ).unwrap();
                // A short (or empty) page means we've reached the end of the tail.
                if (!Array.isArray(batch) || batch.length < UNCONFIRMED_TAIL_PAGE_SIZE) return;
                if (page === UNCONFIRMED_TAIL_MAX_PAGES - 1) {
                    console.warn(
                        `[getPendingUserMessages] unconfirmed tail hit the ${UNCONFIRMED_TAIL_MAX_PAGES}-page cap ` +
                        `(~${UNCONFIRMED_TAIL_MAX_PAGES * UNCONFIRMED_TAIL_PAGE_SIZE} rows); older unconfirmed rows may be unloaded.`
                    );
                }
            }
        };
        // 'expired' joins pending/failed as an UNRESOLVED status the planner always shows
        // (see @common/services/planner-filters): a raw-'expired' doc was listed in the
        // calendar's past statuses but nothing ever FETCHED it, so "Failed only" could not
        // match it on either surface.
        //
        // BOUNDED, unlike pending/failed. 'expired' is written by the max-send-window reaper
        // and is exactly the kind of terminal row that accumulates on a heavy account — the
        // same shape that made the #166 unconfirmed tail need pagination. Newest-first with a
        // cap that LOGS when reached, so this can never silently balloon redux.
        const EXPIRED_LIMIT = 2000;
        const loadExpired = async () => {
            const batch = await dispatch(
                DBUserMessageThunks.find({
                    $or: [
                        { deleted: { $ne: true } },
                    ],
                    // `expired` and `error`: terminal failures that accumulate (PLANNER_LOAD_PLAN).
                    status: { $in: [...PLANNER_LOAD_PLAN.eagerBounded] },
                    $sort: { dueDate: -1 },
                    $limit: EXPIRED_LIMIT,
                    "contactList.0._id": { $exists: true },
                })
            ).unwrap();
            if (Array.isArray(batch) && batch.length >= EXPIRED_LIMIT) {
                console.warn(
                    `[getPendingUserMessages] expired rows hit the ${EXPIRED_LIMIT}-row cap; ` +
                    `older expired rows are not loaded.`
                );
            }
        };
        const expiredPromise = loadExpired().catch(() => { /* independent, like the tail */ });

        // Start immediately (issues page 0 before we await pending/failed), and
        // swallow its rejection so it stays independent of the pending/failed load.
        const unconfirmedTailPromise = loadUnconfirmedTail().catch(() => { /* independent */ });

        // NOTE (§3): sent history (a delivered send is a soft-deleted row) is NOT loaded here. A
        // heavy account holds tens of thousands of them. It loads only while "Show sent" is on,
        // for the visible range, via loadSentHistoryForRange (planner-sent-history.ts).
        // Per-chat, the §4a panel loads only the open chat's rows.

        const userMessages = await pendingFailedPromise;

        //TODO: remove this after migration and also await - migration is 12.2.2026
        for (const message of userMessages) {
            if (message?.contactList?.[0]?.id?.includes("@lid")) {

                const convertedId = await ContentMessageServiceActions.convertWaLidSerializedToCusSerialized(message.contactList[0].id);
                console.log("convertedId", convertedId);
                if (convertedId?.includes("@c.us")) {
                    // Mongo dot-notation update — typed via the shared
                    // `MongoUpdate<T>` helper (common/types/mongo-update.ts,
                    // #42) since UserMessage's strict shape doesn't list
                    // dot keys.
                    const entity: MongoUpdate<UserMessage> = {
                        _id: message._id,
                        "contactList.0.id": convertedId,
                    };
                    dispatch(DBUserMessageThunks.patch({ entity }));
                }
            }

        }

        // Ensure the full unconfirmed tail has finished loading before we resolve,
        // so the badge/status logic sees every unconfirmed row (already swallowed
        // its own rejection, so this never throws — independence preserved).
        await unconfirmedTailPromise;
        await expiredPromise;

        return userMessages;
    }
);

/**
 * Re-syncs the open chat's scheduled-messages panel with the server.
 *
 * The panel shows pending / failed / unconfirmed rows straight out of redux. When a
 * message is sent the backend patches it to `confirmed` + `deleted: true` and it leaves
 * the queue — but that patch does not always reach an open client live, so the row keeps
 * rendering at its last-seen status until a full page refresh rebuilds the store. Users
 * read those rows as messages that never went out.
 *
 * A plain refetch cannot fix it (the server just omits the row; the upserting DB thunks
 * leave the stale copy in place), so this fetches the chat's live queue and PRUNES the
 * panel rows the server no longer reports. The id snapshot is taken BEFORE the request so
 * a message created while it was in flight is never pruned — see staleChatMessageIds.
 */
export const refreshChatScheduledMessagesThunk = createAsyncThunk<
    void,
    void,
    { state: CommonRootState }
>(
    "refreshChatScheduledMessages",
    async (_, { dispatch, getState }) => {
        const state = getState();
        const currentChatId = getSerialized(state.WhatsAppReducer.currentChat?.id);
        if (!currentChatId) return;

        // #165 — the stored recipient id and the open chat's id can disagree on
        // @lid vs @c.us form, so query the whole equivalence set (same resolution
        // the display selector and the cancel path use).
        const chatIdEquivalents = resolveSenderJidEquivalents(
            currentChatId,
            selectMergedContactMap(state),
        );
        if (!chatIdEquivalents.length) return;

        // The server query below can only match on contactList[0], while the display
        // selector matches ANY recipient entry. Restricting the prune candidates to
        // first-contact rows keeps the two in step: a row the query could never return
        // is never a prune candidate either.
        const equivalentSet = new Set(chatIdEquivalents);
        const isPruneCandidate = (m: UserMessage) => equivalentSet.has(m.contactList?.[0]?.id ?? '');
        const snapshotIds = selectMessagesForCurrentChat(state).filter(isPruneCandidate).map((m) => m._id);

        const serverMessages = await dispatch(
            DBUserMessageThunks.find({
                $or: [
                    { deleted: { $ne: true } },
                ],
                // Exactly the statuses the panel displays (selectMessagesForCurrentChat).
                status: {
                    $in: ["pending", "failed", "sent_pending_ack", "sent"],
                },
                "contactList.0.id": { $in: chatIdEquivalents },
                $sort: { dueDate: 1 },
                $limit: -1,
            })
        ).unwrap();

        // The find thunk SWALLOWS its errors (DBServiceReducers' `.catch` logs and
        // returns undefined), so a network failure / 401 resolves as `undefined`
        // rather than rejecting. Coercing that to an empty list would mean "the
        // server queues nothing for this chat" and prune EVERY row in the panel.
        // Only an actual array is evidence about what the server holds.
        if (!Array.isArray(serverMessages)) {
            console.warn('[refreshChatScheduledMessages] refetch returned no array — skipping prune (no evidence to prune on)');
            return;
        }

        const staleIds = staleChatMessageIds({
            snapshotIds,
            localIds: selectMessagesForCurrentChat(getState()).filter(isPruneCandidate).map((m) => m._id),
            serverIds: serverMessages.map((m: UserMessage) => m._id),
            isTempId,
        });

        if (staleIds.length) {
            console.log(`[refreshChatScheduledMessages] pruning ${staleIds.length} stale row(s) the server no longer queues`);
            dispatch(userMessageReducerActions.removeMany(staleIds));
        }
    }
);

// Thunk to send all stuck messages for a campaign
export const sendAllStuckMessages = createAsyncThunk(
    'campaign/sendAllStuckMessages',
    async (campaignId: string, { dispatch, getState }) => {
        const stuckMessages = getCampaignStuckMessages(getState(), campaignId);

        // update message dueDate to now
        for (const message of stuckMessages) {
            dispatch(DBUserMessageThunks.patch({
                entity: {
                    _id: message._id,
                    dueDate: moment().toDate(),
                    // FE#447: "now" was read off this machine's clock.
                    ...clientClockFields(),
                }
            }));
        }
    }
);

export const focusContactInCalendarThunk = createAsyncThunk(
    "focusContactInCalendar",
    async (contactId: string, { dispatch, getState }) => {
        const state = getState() as CommonRootState;
        const messages = userMessagesSelectByQuery(state.DBUserMessageReducer, {
            "contactList.0.id": contactId,
            status: "pending",
            $or: [{ deleted: { $exists: false } }, { deleted: false }]
        }, undefined, undefined);

        if (messages.length > 0) {
            // Sort by date and get the earliest message
            const firstMessage = messages.sort((a, b) =>
                moment(a.dueDate).valueOf() - moment(b.dueDate).valueOf()
            )[0];

            // Open calendar view and navigate to the date of the first message
            dispatch(mainSliceActions.toggleCalendarView(true));
            dispatch(mainSliceActions.setViewMonthStart(moment(firstMessage.dueDate).toDate()));
        }

        // Highlight the contact's messages
        dispatch(setHighlightedContactId(contactId));

        // After 0.5 seconds, remove the highlight
        setTimeout(() => {
            dispatch(setHighlightedContactId(undefined));
        }, 500);
    }
);

export const getScheduledMessagesInRedisThunk = createAsyncThunk(
    'campaign/getScheduledMessagesInRedisThunk',
    async (campaignId: string, { rejectWithValue, dispatch, getState }) => {
        try {

            const state = getState() as any;
            const accessToken = state.UserReducer?.accessToken;

            if (!accessToken) {
                throw new Error('No access token available');
            }

            const response = await fetch(`${appConfig.API_ENDPOINT}/actions?cmd=get-scheduled-messages-in-redis`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`,
                },
                body: JSON.stringify({ campaignId }),
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch scheduled messages: ${response.status} ${response.statusText}`);
            }

            const data = await response?.json();
            if (!data || !data.messagesInRedis) {
                return [];
            }

            const messagesInRedis = data?.messagesInRedis.map((message: { _id: string; text: string; createdAt: string | number | Date }) => ({
                id: message._id,
                contactName: message.text,
                createdAt: new Date(message.createdAt),
                status: "Waiting In Redis",
            }));
            const newObj = {
                [campaignId]: messagesInRedis
            }
            console.log('Payload:', newObj);

            dispatch(userMessageActions.setUserMessagesInRedisByCampaignId(newObj));

        } catch (error) {
            console.error('Error fetching scheduled messages:', error);
            return rejectWithValue((error as { message?: string })?.message || 'Unknown error');
        }
    }
);

export const deleteUserMessage = createAsyncThunk(
    'campaign/deleteUserMessage',
    async (userMessageId: string, { dispatch, getState }) => {
        // Awaited on purpose: callers (see applyRecurringEditPlan, FE#151) rely on this
        // resolving only once the delete has actually been persisted.
        await dispatch(DBUserMessageThunks.delete({
            entity: {
                _id: userMessageId,
            }
        }));
    }
);

// Thunk to delete multiple messages by their IDs
export interface DeleteMessagesByStatusThunkParams {
    messageIds: string[];
}

export const deleteMessagesByStatusThunk = createAsyncThunk<
    void,
    DeleteMessagesByStatusThunkParams,
    { state: CommonRootState; dispatch: AppThunkDispatch }
>(
    'campaign/deleteMessagesByStatus',
    async ({ messageIds }, { dispatch }) => {
        // Delete messages one by one using the existing delete thunk
        await Promise.all(
            messageIds.map((messageId) =>
                dispatch(deleteUserMessage(messageId))
            )
        );
    }
);

// Thunk to delete all messages by status for current chat
export interface DeleteAllMessagesByStatusThunkParams {
    status: 'pending' | 'failed';
    /** #708 — leave recurring series untouched (deleting a series head ends the series). */
    skipRecurring?: boolean;
}

export const deleteAllMessagesByStatusThunk = createAsyncThunk<
    void,
    DeleteAllMessagesByStatusThunkParams,
    { state: CommonRootState; dispatch: AppThunkDispatch }
>(
    'campaign/deleteAllMessagesByStatus',
    async ({ status, skipRecurring }, { dispatch, getState }) => {
        const state = getState();
        const messages = selectMessagesForCurrentChat(state);

        // Filter messages by status
        const messageIds = messages
            .filter((msg) => msg.status === status && !(skipRecurring && msg.isRecurring))
            .map((msg) => msg._id)
            .filter((id): id is string => !!id);

        if (messageIds.length > 0) {
            await dispatch(deleteMessagesByStatusThunk({ messageIds }));
        }
    }
);

// Thunk to delete all CLEARABLE messages for the current chat (pending/failed).
// Deliberately NOT every message the sidebar displays — see
// selectClearableMessagesForCurrentChat (#166/#149).
export interface DeleteAllMessagesForCurrentChatParams {
    /** #708 — leave recurring series untouched (deleting a series head ends the series). */
    skipRecurring?: boolean;
}
export const deleteAllMessagesForCurrentChatThunk = createAsyncThunk<
    void,
    DeleteAllMessagesForCurrentChatParams | void,
    { state: CommonRootState; dispatch: AppThunkDispatch }
>(
    'campaign/deleteAllMessagesForCurrentChat',
    async (params, { dispatch, getState }) => {
        const state = getState();
        const skipRecurring = !!params && params.skipRecurring === true;
        const messages = selectClearableMessagesForCurrentChat(state)
            .filter((msg) => !(skipRecurring && msg.isRecurring));

        const messageIds = messages
            .map((msg) => msg._id)
            .filter((id): id is string => !!id);

        if (messageIds.length > 0) {
            await dispatch(deleteMessagesByStatusThunk({ messageIds }));
        }
    }
);


// cfreate params type for handleMessageChangeThunk
type HandleMessageChangeThunkParams = {
    message: UserMessage;
    msg: UserMessage;
}

export const handleMessageChangeThunk = createAsyncThunk(
    'campaign/handleMessageChangeThunk',
    async ({ message, msg }: HandleMessageChangeThunkParams, { dispatch }) => {
        // REMOVED: setIsUpdatingLocalMessageState calls - they cause unnecessary re-renders during typing
        // This thunk is for internal state synchronization, not user-facing operations

        // Create a complete message state that preserves all necessary fields
        const updatedMessage: ExtendedUserMessage = {
            ...message,
            ...msg,
            isPoll: msg.isPoll,
            // poll is optional/undefined; coalesce nulls to undefined to match the schema.
            poll: msg.poll ?? message.poll ?? undefined,
            message: msg.message || message.message,
            asset: msg.hasOwnProperty('asset') ? msg.asset : message.asset, // Allow undefined to delete asset
            imageId: (msg.hasOwnProperty('imageId') ? msg.imageId : message.imageId) ?? undefined // Allow undefined to delete imageId
        };

        // Update the message in Redux store
        dispatch(addScheduledMessageButtonActions.updateMessage(updatedMessage));
    }
);

export const handleAddScheduledMessageCloseThunk = createAsyncThunk(
    'campaign/handleAddScheduledMessageCloseThunk',
    async (_, { dispatch }) => {
        dispatch(addScheduledMessageButtonActions.closeDialog());
        dispatch(addScheduledMessageButtonActions.setPollFromWAModal(undefined));
        dispatch(setAddMsgInCalendarContact(null));
        dispatch(setIsReply(null));
        dispatch(setMentions(null));
        dispatch(scheduledMessageEditorActions.resetScheduledMessageEditor());
    }
);



export const checkIfReplyingToMessageThunk = createAsyncThunk(
    'campaign/checkIfReplyingToMessageThunk',
    async (_, { dispatch }) => {
        const replyData: any = await ContentMessageServiceActions.checkIfReplyingToMessage();
        dispatch(setIsReply(replyData));
    }
);
